/**
 * Pay Bills, the check and the rail each method routes through (piece 24;
 * decisions 0032, 0195, 0225).
 *
 * 🔨 Pay Bills is one Payment against as many Bills as it covers, and it is
 * the existing `Payment` record pointed outward, never a second mechanism.
 * The payment carries `direction: Sent` and one `PaymentAllocation` row per
 * bill; `Bill.amountPaid` derives from those rows and is never maintained.
 */

import { roundHalfUpCents } from '../money';
import { t } from '../strings';

/** The rails a `PaymentGatewayBinding` can name; a wire is recorded as method Other with the rail in `reference`. */
export const PAYMENT_RAILS = ['Card', 'ACH', 'Terminal'] as const;
export type PaymentRailValue = (typeof PAYMENT_RAILS)[number];

/** The methods a Pay Bills run can pay by; the enum is `Payment.method`'s and carries no Wire member. */
export const PAY_BILLS_METHODS = ['Check', 'ACH', 'Card', 'Cash', 'Other'] as const;
export type PayBillsMethod = (typeof PAY_BILLS_METHODS)[number];

/** One bill a run pays, as the run reads it: what it still owes and who it is owed to. */
export interface PayableBill {
  billId: string;
  billNumber: string;
  vendorCompanyId: string;
  openBalanceCents: number;
}

/** What a run asks for: an amount against each bill, or nothing named and the full open balance taken. */
export interface PayBillsLine {
  billId: string;
  amountCents?: number | null;
}

/**
 * 🔨 One run pays the bills of one vendor. A Payment names one Company, and a
 * run spanning two vendors would have to write two payments, which is two
 * runs. The refusal names both vendors rather than silently paying one.
 */
export function payBillsProblem(input: { bills: readonly PayableBill[]; lines: readonly PayBillsLine[]; method: string; bankAccountId?: string | null }): string | null {
  if (input.lines.length === 0) return 'a payment run names at least one bill; tick the bills this run pays';
  if (!(PAY_BILLS_METHODS as readonly string[]).includes(input.method)) return `a payment run pays by ${PAY_BILLS_METHODS.join(', ')}; ${input.method || 'nothing'} is not one of them`;
  if (!input.bankAccountId) return 'a payment run names the account the money leaves from; choose the bank account paying this run';
  const vendors = new Set(input.bills.map((bill) => bill.vendorCompanyId));
  if (vendors.size > 1) return 'one payment run pays one vendor; these bills are owed to more than one, so they need a run each';
  for (const line of input.lines) {
    const bill = input.bills.find((candidate) => candidate.billId === line.billId);
    if (!bill) return 'a payment run names a bill it was not given; reload the payable bills and pick them again, because the screen and the server disagree about what is on this run';
    const amount = line.amountCents ?? bill.openBalanceCents;
    if (!Number.isInteger(amount) || amount <= 0) return `${bill.billNumber} takes an amount above zero, in whole cents`;
    if (amount > bill.openBalanceCents) return `${bill.billNumber} owes less than the amount against it; it still owes ${bill.openBalanceCents} cents`;
  }
  return null;
}

/** The allocations a run writes, one per bill, each defaulting to what that bill still owes. */
export function payBillsAllocations(bills: readonly PayableBill[], lines: readonly PayBillsLine[]): { billId: string; amountCents: number }[] {
  return lines.map((line) => {
    const bill = bills.find((candidate) => candidate.billId === line.billId)!;
    return { billId: line.billId, amountCents: line.amountCents ?? bill.openBalanceCents };
  });
}

/** The run's own total: the sum of what it allocates, which is what the Payment's amount is. */
export function payBillsTotal(allocations: readonly { amountCents: number }[]): number {
  return allocations.reduce((sum, row) => sum + row.amountCents, 0);
}

/**
 * 24% of every reportable payment, withheld from the first dollar where the
 * vendor carries the flag (piece 24, Filing Status). The rate is the IRS's and
 * is not configurable; the withheld amount posts to 2150 and the vendor is
 * paid the remainder.
 */
export const BACKUP_WITHHOLDING_RATE = 0.24;

export function backupWithheldCents(amountCents: number, required: boolean): number {
  return required ? roundHalfUpCents(amountCents * BACKUP_WITHHOLDING_RATE) : 0;
}

/**
 * What the ACH rail can do right now (decision 0225). 🔨 ACH is initiated
 * through the payment seam and never through a bank file exported by hand.
 * 💰 Until a provider is bound to the ACH rail, the payment is initiated at
 * the bank and recorded here, and the Pay Bills screen says so.
 */
export type AchRail =
  | { kind: 'seam'; providerKey: string }
  | { kind: 'recordedHere'; notice: string };

export function achRailFor(bindings: readonly { method: string; providerKey: string; isActive: boolean }[]): AchRail {
  const bound = bindings.find((row) => row.method === 'ACH' && row.isActive);
  return bound ? { kind: 'seam', providerKey: bound.providerKey } : { kind: 'recordedHere', notice: t('payBills.achRecordedHere') };
}

/**
 * 🔨 A bank file is never the answer. The seam is the one way an ACH leaves,
 * and where no provider is bound the honest outcome is a recorded payment,
 * not an export somebody keys in twice.
 */
export function achExportRefusal(): string {
  return t('payBills.achNoExport');
}

/** The paying account a method resolves to: a card pays from a real Credit Card Account, never a bare label. */
export function payingAccountProblem(method: string, account: { kind: string; isActive: boolean } | null): string | null {
  if (!account) return 'the account the money leaves from does not exist';
  if (!account.isActive) return 'that account is closed; a payment leaves an open account';
  // 🔨 A card payment is a real transaction on that card's own feed, so the paying account is a Credit Card Account (piece 24, Pay Bills).
  if (method === 'Card' && account.kind !== 'CreditCard') return 'paying by card resolves to a Credit Card Account, so the charge appears on that card’s own feed and can be matched like any other';
  if (method !== 'Card' && account.kind === 'CreditCard') return 'a credit card account pays by card; a check or a transfer leaves a bank account';
  return null;
}

/**
 * 🔨 Whether a printed batch may be confirmed against a row, and the memo the
 * sheet carries, live in `checkBatch.ts` beside the rest of the batch's rules
 * (decision 0556). A check's number comes off the stock, so nothing here
 * allocates one.
 */

/** 🔨 A check is voided in place with a reason, never deleted; the row and its number stay. */
export function checkVoidProblem(status: string, reason: string | null | undefined): string | null {
  if (status === 'Voided') return 'this check is already void';
  if (!reason?.trim()) return 'voiding a check names the reason, because the row and its number stay';
  return null;
}
